import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import Navbar from '../components/Navbar';
import EvidenceCard from '../components/EvidenceCard';
import SkepticsCorner from '../components/SkepticsCorner';
import DisclaimerBanner from '../components/DisclaimerBanner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Star, Trash2, Download, ChevronDown, ChevronUp, Search, History as HistoryIcon } from 'lucide-react';
import { toast } from 'sonner';

const HistoryPage = () => {
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    loadQueries();
  }, [activeTab]);

  const loadQueries = async () => {
    setLoading(true);
    try {
      const response = await api.getQueries(activeTab === 'starred');
      setQueries(response.data);
    } catch (err) {
      toast.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  const handleStar = async (queryId) => {
    try {
      const response = await api.toggleStar(queryId);
      setQueries(prev => prev.map(q => 
        q.id === queryId ? { ...q, is_starred: response.data.is_starred } : q
      ));
      toast.success(response.data.is_starred ? 'Added to favorites' : 'Removed from favorites');
    } catch (err) {
      toast.error('Failed to update favorite');
    }
  };

  const handleDelete = async (queryId) => {
    try {
      await api.deleteQuery(queryId);
      setQueries(prev => prev.filter(q => q.id !== queryId));
      toast.success('Query deleted');
    } catch (err) {
      toast.error('Failed to delete query');
    }
  };

  const handleExport = async (queryId) => {
    try {
      const response = await api.exportQuery(queryId);
      const blob = new Blob([response.data.content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Summary exported');
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <main className="pt-20 pb-16 main-content">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
              <HistoryIcon className="h-5 w-5 text-slate-600" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">Search History</h1>
              <p className="text-sm text-slate-500">{queries.length} queries saved</p>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
            <TabsList className="bg-slate-100 rounded-full p-1">
              <TabsTrigger value="all" className="rounded-full px-4" data-testid="tab-all">
                All Queries
              </TabsTrigger>
              <TabsTrigger value="starred" className="rounded-full px-4" data-testid="tab-starred">
                <Star className="h-4 w-4 mr-1" />
                Favorites
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {loading ? (
            <div className="text-center py-12 text-slate-500">Loading...</div>
          ) : queries.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="py-12 text-center">
                <Search className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-semibold text-slate-900 mb-2">
                  {activeTab === 'starred' ? 'No favorites yet' : 'No search history'}
                </h3>
                <p className="text-sm text-slate-500">
                  {activeTab === 'starred' 
                    ? 'Star queries to save them here' 
                    : 'Your searches will appear here'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {queries.map((query) => (
                <Card 
                  key={query.id} 
                  className="border-slate-200 overflow-hidden"
                  data-testid={`query-card-${query.id}`}
                >
                  <CardContent className="p-0">
                    {/* Header */}
                    <div 
                      className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                      onClick={() => setExpandedId(expandedId === query.id ? null : query.id)}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 mb-1 line-clamp-2">
                            {query.query_summary || query.query_text || 'Evidence search'}
                          </p>
                          <p className="text-xs text-slate-500">
                            {formatDate(query.timestamp)}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {query.is_starred && (
                            <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                          )}
                          {expandedId === query.id ? (
                            <ChevronUp className="h-5 w-5 text-slate-400" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-slate-400" />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Content */}
                    {expandedId === query.id && (
                      <div className="border-t border-slate-100 p-4 bg-slate-50/50">
                        {/* Actions */}
                        <div className="flex items-center gap-2 mb-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleStar(query.id)}
                            className="rounded-full"
                            data-testid={`star-${query.id}`}
                          >
                            <Star className={`h-4 w-4 ${query.is_starred ? 'fill-amber-400 text-amber-400' : ''}`} />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleExport(query.id)}
                            className="rounded-full"
                            data-testid={`export-${query.id}`}
                          >
                            <Download className="h-4 w-4 mr-1" />
                            Export
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(query.id)}
                            className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-${query.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>

                        {/* Key Recommendation */}
                        <div className="mb-4 p-3 bg-sky-50 rounded-lg border border-sky-200">
                          <h4 className="font-semibold text-slate-900 text-sm mb-1">Key Recommendation</h4>
                          <p className="text-sm text-slate-700">
                            {query.response?.key_recommendation || 'No recommendation'}
                          </p>
                        </div>

                        {/* Evidence Cards */}
                        <div className="space-y-3 mb-4">
                          {query.response?.evidence_cards?.slice(0, 2).map((card, i) => (
                            <EvidenceCard key={i} card={card} />
                          ))}
                        </div>

                        {/* Skeptic's Corner */}
                        <SkepticsCorner points={query.response?.skeptics_corner} />
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
      <DisclaimerBanner />
    </div>
  );
};

export default HistoryPage;
